import { gql } from '@apollo/client';

export const USER_FRAGMENT = gql`
  fragment UserFields on User {
    id
    fullName
    firstName
    avatarUrl
    roles
  }
`;

export const USER_FRAGMENT_WITH_PERMISSIONS = gql`
  fragment UserFieldsWithPermissions on User {
    id
    fullName
    firstName
    avatarUrl
    roles
    permissions(resourceId: $communityId, resourceType: Community)
  }
`;

export const MENTIONED_USERS_FRAGMENT = gql`
  fragment MentionedUsersField on UserConnection {
    edges {
      node {
        id
        fullName
        avatarUrl
      }
    }
  }
`;

export const POST_ATTACHMENTS_FRAGMENT = gql`
  fragment PostAttachmentsFields on PostAttachment {
    attachmentType
    fileName
    fileSizeInBytes
    id
    url
    videoProxyId
    altText
    thumbnailUrl
    provider
  }
`;

export const POST_CONTENT_FRAGMENT = (type: 'Post' | 'Reply') => gql`
  fragment PostContentFields on ${type} {
    content
    body {
      jsonContent
      plainText
    }
    createdAt
    editedAt
    isFollowing
    hasReacted @skip(if: $multipleReactionsEnabled)
    myReactions @include(if: $multipleReactionsEnabled)
  }
`;

export const POST_FRAGMENT_WITH_USER_PERMISSIONS = gql`
  fragment PostFieldsWithUserPermissions on Post {
    ...PostContentFields
    body {
      jsonContent
      plainText
    }
    id
    title
    pinnedAt
    communitySpace {
      id
      name
      iconName
      readOnly
    }
    postAttachments {
      ...PostAttachmentsFields
    }
    replies @connection(key: "replies") {
      totalCount
      nodes {
        id
        replies {
          nodes {
            body {
              jsonContent
              plainText
            }
            id
          }
          totalCount
        }
      }
    }
    mentionedUsers @connection(key: "mentionedUsers") {
      ...MentionedUsersField
    }
    postReactions(first: $numberOfReactions)
      @connection(key: "postReactions")
      @skip(if: $multipleReactionsEnabled) {
      totalCount
      edges {
        node {
          user {
            id
            fullName
          }
        }
      }
    }
    reactionStats @include(if: $multipleReactionsEnabled)
    user {
      permissions(resourceId: $communityId, resourceType: Community)
      ...UserFields
    }
  }
  ${USER_FRAGMENT}
  ${MENTIONED_USERS_FRAGMENT}
  ${POST_ATTACHMENTS_FRAGMENT}
  ${POST_CONTENT_FRAGMENT('Post')}
`;

export const POST_FRAGMENT = gql`
  fragment PostFields on Post {
    ...PostContentFields
    id
    title
    pinnedAt
    communitySpace {
      id
      name
      iconName
      readOnly
    }
    postAttachments {
      ...PostAttachmentsFields
    }
    replies @connection(key: "replies") {
      totalCount
      nodes {
        id
        replies {
          nodes {
            id
          }
          totalCount
        }
      }
    }
    mentionedUsers @connection(key: "mentionedUsers") {
      ...MentionedUsersField
    }
    postReactions(first: $numberOfReactions)
      @connection(key: "postReactions")
      @skip(if: $multipleReactionsEnabled) {
      totalCount
      edges {
        node {
          user {
            id
            fullName
          }
        }
      }
    }
    reactionStats @include(if: $multipleReactionsEnabled)
    user {
      ...UserFields
    }
  }
  ${USER_FRAGMENT}
  ${MENTIONED_USERS_FRAGMENT}
  ${POST_ATTACHMENTS_FRAGMENT}
  ${POST_CONTENT_FRAGMENT('Post')}
`;

export const REPLY_FRAGMENT = gql`
  fragment ReplyFields on Reply {
    ...PostContentFields
    id
    postReactions(first: $numberOfReactions)
      @connection(key: "postReactions")
      @skip(if: $multipleReactionsEnabled) {
      totalCount
      edges {
        node {
          user {
            id
            fullName
          }
        }
      }
    }
    reactionStats @include(if: $multipleReactionsEnabled)
    mentionedUsers @connection(key: "mentionedUsers") {
      ...MentionedUsersField
    }
    user {
      ...UserFields
    }
    postAttachments {
      ...PostAttachmentsFields
    }
  }
  ${USER_FRAGMENT}
  ${MENTIONED_USERS_FRAGMENT}
  ${POST_ATTACHMENTS_FRAGMENT}
  ${POST_CONTENT_FRAGMENT('Reply')}
`;

export const REPLY_FRAGMENT_WITH_USER_PERMISSIONS = gql`
  fragment ReplyFieldsWithUserPermissions on Reply {
    ...PostContentFields
    id
    postReactions(first: $numberOfReactions)
      @connection(key: "postReactions")
      @skip(if: $multipleReactionsEnabled) {
      totalCount
      edges {
        node {
          user {
            id
            fullName
          }
        }
      }
    }
    reactionStats @include(if: $multipleReactionsEnabled)
    mentionedUsers @connection(key: "mentionedUsers") {
      ...MentionedUsersField
    }
    user {
      ...UserFieldsWithPermissions
    }
    postAttachments {
      ...PostAttachmentsFields
    }
  }
  ${USER_FRAGMENT_WITH_PERMISSIONS}
  ${MENTIONED_USERS_FRAGMENT}
  ${POST_ATTACHMENTS_FRAGMENT}
  ${POST_CONTENT_FRAGMENT('Reply')}
`;

export const REPLY_EDGE_NODE_REPLIES_FRAGMENT = gql`
  fragment ReplyEdgeNodeReliesFields on ReplyConnection {
    edges {
      cursor
      node {
        ...ReplyFields
      }
    }
    pageInfo {
      hasPreviousPage
      hasNextPage
      startCursor
      endCursor
    }
    totalCount
  }
  ${REPLY_FRAGMENT}
`;

export const REPLY_EDGE_FRAGMENT = gql`
  fragment ReplyEdgeFields on Reply {
    ...ReplyFieldsWithUserPermissions
    replies(last: $numOfNestedReplies, orderBy: { field: CREATED_AT, direction: ASC }) {
      ...ReplyEdgeNodeReliesFields
    }
  }
  ${REPLY_FRAGMENT_WITH_USER_PERMISSIONS}
  ${REPLY_EDGE_NODE_REPLIES_FRAGMENT}
`;

export const MAIN_QUERY = gql`
  query Main($communityId: ID!) {
    site {
      id
      directMessagingEnabled
      newLearnerApplicationEnabled
      name
      favicon
      features {
        directMessaging {
          isEnabled
        }
        communityEvents {
          isEnabled
        }
        communityEventNotifications {
          isEnabled
        }
        newCommunities {
          isEnabled
        }
      }
      filestackPolicy {
        policy
        signature
      }
      fullstory {
        enabled
        orgId
        studentTrackingEnabled
      }
      featureFlags
      subdomain
      trackingKey
      whiteLabellingActivated
      googleAnalyticsKey
      appcuesDisabled
      directMessagingEnabled
      integrations {
        facebookPixelCode
        gaMeasurementId
      }
    }

    community(id: $communityId) {
      id
      name
      accessType
      description
      directMessagingEnabled
      primaryColor
      font
      imageUrl
      s3UploadPath
      published
      directMessagingEnabled
      status
      communityUsers(filter: { active: true }) {
        totalCount
      }
      communitySections {
        edges {
          node {
            communitySpaces {
              totalCount
            }
          }
        }
      }
      mentionableUsers(searchTerm: "", first: 5) {
        edges {
          node {
            id
            user {
              id
              gid
              fullName
              profile {
                id
                avatarUrl
              }
            }
          }
        }
      }
    }

    me {
      id
      gid
      firstName
      lastName
      fullName
      email
      avatarUrl
      roles
      locale
      profile {
        id
        timezone
      }
      permissions(resourceId: $communityId, resourceType: Community)
    }
  }
`;

export const ACCOUNT_NOTIFICATIONS_QUERY = gql`
  query AccountNotifications {
    accountNotifications {
      isEnabled
    }
  }
`;
